"use client";

import { c as _c } from "react/compiler-runtime";
import { useT, useGameLocale } from "@/lib/i18n";
import { Suspense, useState, useEffect } from "react";
import { Link } from "@/i18n/navigation";
import { useSearchParams } from "next/navigation";
import { useBetaPrefix } from "@/lib/use-lang-prefix";
import { RUNS_BROWSE, browseRowHref, type BrowseConfig } from "@/lib/browse-config";
import ReplaySummary from "../replays/ReplaySummary";
const API = process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:8000";
interface BrowseRun {
  run_hash: string;
  character: string;
  ascension: number;
  win: boolean;
  was_abandoned?: boolean;
  username?: string;
  deck_size: number;
  relic_count: number;
  floors_reached: number;
  run_time: number;
  build_id?: string;
  seed?: string;
  replay_url?: string;
}
interface CharacterNameRow {
  id: string;
  name: string;
}
const CHARACTERS = ["Ironclad", "Silent", "Defect", "Necrobinder", "Regent"] as const;
function cleanId(id: string): string {
  return id.replace(/^(CHARACTER|CARD|RELIC|ENCOUNTER|EVENT|MONSTER|ACT|POTION)\./, "");
}
function displayName(id: string): string {
  return cleanId(id).replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
}
function stripThe(name: string): string {
  return name.replace(/^the\s+/i, "");
}
function formatTimeShort(s: number): string {
  const m = Math.floor(s / 60);
  return `${m}m`;
}

// Parse an ascension value: "20" → exact, "3-4" → range, "3+" → min.
// Tolerates the in-game "A10" spelling ("a10", "a3-a7").
function parseAscension(value: string): {
  exact?: number;
  min?: number;
  max?: number;
} {
  const v = value.trim().replace(/a(?=\d)/gi, "");
  if (!v) return {};
  const range = /^(\d+)\s*-\s*(\d+)$/.exec(v);
  if (range) {
    const lo = parseInt(range[1], 10);
    const hi = parseInt(range[2], 10);
    return {
      min: Math.min(lo, hi),
      max: Math.max(lo, hi)
    };
  }
  const min = /^(\d+)\s*\+$/.exec(v);
  if (min) return {
    min: parseInt(min[1], 10)
  };
  const num = parseInt(v, 10);
  if (!Number.isNaN(num)) return {
    exact: num
  };
  return {};
}
type Mode = "" | "single" | "multi";
type GameMode = "" | "standard" | "daily" | "daily_today" | "custom";
type WinFilter = "" | "true" | "false";
type Sort = "date" | "time_asc" | "time_desc" | "ascension_desc";

// Parse `key:value` expressions out of a free-text query.
// Returns extracted filters and the remaining free-text (after stripping
// recognized key:value pairs).
type QueryKey = "user" | "seed" | "char" | "asc" | "version" | "mode" | "result" | "players" | "card" | "relic" | "shop" | "winrate";
function parseQuery(q: string): {
  filters: Partial<Record<QueryKey, string>>;
  rest: string;
} {
  const filters: Record<string, string> = {};
  const tokens = q.match(/(\w+):"[^"]+"|\w+:[\S]+|[^\s]+/g) || [];
  const restTokens: string[] = [];
  // card/relic accumulate across repeated tokens AND comma-separated values
  const appendMulti = (existing: string | undefined, value: string) => existing ? `${existing},${value}` : value;
  for (let i = 0; i < tokens.length; i++) {
    let tok = tokens[i];
    // Tolerate a space after the colon ("asc: 10"): a bare "key:" token
    // absorbs the next token as its value. Without this the pair fell
    // through to the username search and silently matched nothing.
    if (/^[a-z]+:$/i.test(tok) && tokens[i + 1] && !tokens[i + 1].includes(":")) {
      tok = tok + tokens[i + 1];
      i++;
    }
    const m = /^([a-z]+):"?([^"]+)"?$/i.exec(tok);
    if (!m) {
      restTokens.push(tok);
      continue;
    }
    const key = m[1].toLowerCase();
    const value = m[2];
    if (["user", "username", "u"].includes(key)) filters.user = value;else if (["seed", "s"].includes(key)) filters.seed = value;else if (["char", "character", "c"].includes(key)) filters.char = value;else if (["asc", "ascension", "a"].includes(key)) filters.asc = value;else if (["version", "v", "build"].includes(key)) filters.version = value;else if (["mode", "gamemode"].includes(key)) filters.mode = value;else if (["result", "win"].includes(key)) filters.result = value;else if (["players", "p"].includes(key)) filters.players = value;else if (["card", "cards"].includes(key)) filters.card = appendMulti(filters.card, value);else if (["relic", "relics"].includes(key)) filters.relic = appendMulti(filters.relic, value);else if (["shop", "bought", "buy"].includes(key)) filters.shop = appendMulti(filters.shop, value);else if (["winrate", "wr"].includes(key)) filters.winrate = value;else restTokens.push(tok);
  }
  return {
    filters,
    rest: restTokens.join(" ").trim()
  };
}

// "50-70" -> {min:50, max:70}; "100" -> {min:100, max:100}; "60-" / "60+" /
// ">=60" -> {min:60}; "-40" / "<=40" -> {max:40}; ">50" excludes exactly-50
// via a small epsilon, "<50" likewise. A trailing % is tolerated everywhere.
// Anything non-numeric parses to {} and is ignored.
function parseWinrate(expr: string): {
  min?: number;
  max?: number;
} {
  const clamp = (n: number) => Math.min(100, Math.max(0, n));
  const e = expr.trim().replace(/%/g, "");
  const cmp = /^(>=|<=|>|<)\s*(\d{1,3}(?:\.\d+)?)$/.exec(e);
  if (cmp) {
    const v = clamp(parseFloat(cmp[2]));
    if (cmp[1] === ">=") return {
      min: v
    };
    if (cmp[1] === "<=") return {
      max: v
    };
    if (cmp[1] === ">") return {
      min: clamp(v + 0.01)
    };
    return {
      max: clamp(v - 0.01)
    };
  }
  const plus = /^(\d{1,3}(?:\.\d+)?)\+$/.exec(e);
  if (plus) return {
    min: clamp(parseFloat(plus[1]))
  };
  const range = /^(\d{1,3}(?:\.\d+)?)?\s*-\s*(\d{1,3}(?:\.\d+)?)?$/.exec(e);
  if (range && (range[1] !== undefined || range[2] !== undefined)) {
    const out: {
      min?: number;
      max?: number;
    } = {};
    if (range[1] !== undefined) out.min = clamp(parseFloat(range[1]));
    if (range[2] !== undefined) out.max = clamp(parseFloat(range[2]));
    return out;
  }
  const single = /^(\d{1,3}(?:\.\d+)?)$/.exec(e);
  if (single) {
    const v = clamp(parseFloat(single[1]));
    return {
      min: v,
      max: v
    };
  }
  return {};
}

// Expand a version expression to the list of build_ids it covers.
// "v0.104.0-v0.106.0" → all versions between (inclusive), using the
// already numeric-sorted `versions` list. A single version returns [it].
function expandVersionRange(expr: string, versions: string[]): string[] {
  const range = expr.split("-").map(s => s.trim());
  if (range.length !== 2) return versions.includes(expr) ? [expr] : [expr];
  const [a, b] = range;
  const ia = versions.indexOf(a);
  const ib = versions.indexOf(b);
  if (ia === -1 || ib === -1) return [expr];
  const [lo, hi] = ia <= ib ? [ia, ib] : [ib, ia];
  return versions.slice(lo, hi + 1);
}
function BrowseRunsClientInner(t0) {
  const $ = _c(335);
  const {
    config
  } = t0;
  const bp = useBetaPrefix();
  const lang = useGameLocale();
  const t = useT();
  const searchParams = useSearchParams();
  let t1;
  if ($[0] !== searchParams) {
    t1 = () => searchParams.get("q") || "";
    $[0] = searchParams;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [query, setQuery] = useState(t1);
  let t2;
  if ($[2] !== searchParams) {
    t2 = () => searchParams.get("character") || "";
    $[2] = searchParams;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const [character, setCharacter] = useState(t2);
  let t3;
  if ($[4] !== searchParams) {
    t3 = () => {
      const v = searchParams.get("win");
      return v === "true" || v === "false" ? v : "";
    };
    $[4] = searchParams;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const [win, setWin] = useState(t3);
  let t4;
  if ($[6] !== searchParams) {
    t4 = () => searchParams.get("username") || "";
    $[6] = searchParams;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const [user, setUser] = useState(t4);
  let t5;
  if ($[8] !== searchParams) {
    t5 = () => searchParams.get("seed") || "";
    $[8] = searchParams;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const [seed, setSeed] = useState(t5);
  let t6;
  if ($[10] !== searchParams) {
    t6 = () => searchParams.get("build_id") || "";
    $[10] = searchParams;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const [buildId, setBuildId] = useState(t6);
  let t7;
  if ($[12] !== searchParams) {
    t7 = () => {
      const v_0 = searchParams.get("players");
      return v_0 === "single" || v_0 === "multi" ? v_0 : "";
    };
    $[12] = searchParams;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const [mode, setMode] = useState(t7);
  let t8;
  if ($[14] !== searchParams) {
    t8 = () => {
      const v_1 = searchParams.get("game_mode");
      if (v_1 === "daily" || v_1 === "custom" || v_1 === "standard" || v_1 === "daily_today") {
        return v_1;
      }
      return "";
    };
    $[14] = searchParams;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const [gameMode, setGameMode] = useState(t8);
  let t9;
  if ($[16] !== searchParams) {
    t9 = () => searchParams.get("ascension") || "";
    $[16] = searchParams;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  const [ascension, setAscension] = useState(t9);
  let t10;
  if ($[18] !== searchParams) {
    t10 = () => {
      const v_2 = searchParams.get("sort");
      if (v_2 === "date" || v_2 === "time_asc" || v_2 === "time_desc" || v_2 === "ascension_desc") {
        return v_2;
      }
      return "date";
    };
    $[18] = searchParams;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  const [sort, setSort] = useState(t10);
  const [page, setPage] = useState(1);
  let t11;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = [];
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  const [runs, setRuns] = useState(t11);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  let t12;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = [];
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  const [versions, setVersions] = useState(t12);
  let t13;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = {};
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  const [charNames, setCharNames] = useState(t13);
  let t14;
  let t15;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = () => {
      fetch(`${API}/api/runs/versions`).then(_temp).then(data => {
        const filtered = (data.versions || []).filter(_temp2).sort(_temp3);
        setVersions(filtered);
      }).catch(_temp4);
    };
    t15 = [];
    $[23] = t14;
    $[24] = t15;
  } else {
    t14 = $[23];
    t15 = $[24];
  }
  useEffect(t14, t15);
  let t16;
  let t17;
  if ($[25] !== lang) {
    t16 = () => {
      fetch(`${API}/api/characters?lang=${lang}`).then(_temp5).then(data_0 => {
        const m = {};
        for (const c of data_0) {
          m[c.id.toUpperCase()] = c.name;
        }
        setCharNames(m);
      }).catch(_temp6);
    };
    t17 = [lang];
    $[25] = lang;
    $[26] = t16;
    $[27] = t17;
  } else {
    t16 = $[26];
    t17 = $[27];
  }
  useEffect(t16, t17);
  let t18;
  if ($[28] !== charNames) {
    t18 = function charName(id) {
      return charNames[id.toUpperCase()] ?? displayName(`CHARACTER.${id}`);
    };
    $[28] = charNames;
    $[29] = t18;
  } else {
    t18 = $[29];
  }
  const charName = t18;
  let queryFilters;
  let rest;
  let t19;
  if ($[30] !== character || $[31] !== query) {
    ({
      filters: queryFilters,
      rest
    } = parseQuery(query));
    t19 = character || (queryFilters.char ? queryFilters.char.toUpperCase() : "");
    $[30] = character;
    $[31] = query;
    $[32] = queryFilters;
    $[33] = rest;
    $[34] = t19;
  } else {
    queryFilters = $[32];
    rest = $[33];
    t19 = $[34];
  }
  const effectiveChar = t19;
  const effectiveWin = win || (queryFilters.result === "win" ? "true" : queryFilters.result === "loss" ? "false" : "");
  const effectiveUser = user || queryFilters.user || rest || "";
  const effectiveSeed = seed || queryFilters.seed || "";
  const effectiveBuildId = buildId || queryFilters.version || "";
  const effectiveAscension = ascension || queryFilters.asc || "";
  const effectivePlayers = mode || (queryFilters.players === "single" || queryFilters.players === "multi" ? queryFilters.players : "");
  const effectiveGameMode = gameMode || (queryFilters.mode === "daily" || queryFilters.mode === "custom" || queryFilters.mode === "standard" ? queryFilters.mode : "");
  const effectiveCard = queryFilters.card || "";
  const effectiveRelic = queryFilters.relic || "";
  const effectiveShop = queryFilters.shop || "";
  const effectiveWinrate = queryFilters.winrate || "";
  let t20;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = () => {
      setPage(1);
    };
    $[35] = t20;
  } else {
    t20 = $[35];
  }
  let t21;
  if ($[36] !== ascension || $[37] !== buildId || $[38] !== character || $[39] !== gameMode || $[40] !== mode || $[41] !== query || $[42] !== seed || $[43] !== sort || $[44] !== user || $[45] !== win) {
    t21 = [query, character, win, user, seed, buildId, mode, gameMode, ascension, sort];
    $[36] = ascension;
    $[37] = buildId;
    $[38] = character;
    $[39] = gameMode;
    $[40] = mode;
    $[41] = query;
    $[42] = seed;
    $[43] = sort;
    $[44] = user;
    $[45] = win;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  useEffect(t20, t21);
  let t22;
  let t23;
  if ($[47] !== ascension || $[48] !== buildId || $[49] !== character || $[50] !== gameMode || $[51] !== mode || $[52] !== query || $[53] !== seed || $[54] !== sort || $[55] !== user || $[56] !== win) {
    t22 = () => {
      const params = new URLSearchParams();
      if (query) {
        params.set("q", query);
      }
      if (character) {
        params.set("character", character);
      }
      if (win) {
        params.set("win", win);
      }
      if (user) {
        params.set("username", user);
      }
      if (seed) {
        params.set("seed", seed);
      }
      if (buildId) {
        params.set("build_id", buildId);
      }
      if (mode) {
        params.set("players", mode);
      }
      if (gameMode) {
        params.set("game_mode", gameMode);
      }
      if (ascension) {
        params.set("ascension", ascension);
      }
      if (sort !== "date") {
        params.set("sort", sort);
      }
      const qs = params.toString();
      const url = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
      window.history.replaceState(null, "", url);
    };
    t23 = [query, character, win, user, seed, buildId, mode, gameMode, ascension, sort];
    $[47] = ascension;
    $[48] = buildId;
    $[49] = character;
    $[50] = gameMode;
    $[51] = mode;
    $[52] = query;
    $[53] = seed;
    $[54] = sort;
    $[55] = user;
    $[56] = win;
    $[57] = t22;
    $[58] = t23;
  } else {
    t22 = $[57];
    t23 = $[58];
  }
  useEffect(t22, t23);
  let t24;
  let t25;
  if ($[59] !== config.endpoint || $[60] !== effectiveAscension || $[61] !== effectiveBuildId || $[62] !== effectiveCard || $[63] !== effectiveChar || $[64] !== effectiveGameMode || $[65] !== effectivePlayers || $[66] !== effectiveRelic || $[67] !== effectiveSeed || $[68] !== effectiveShop || $[69] !== effectiveUser || $[70] !== effectiveWin || $[71] !== effectiveWinrate || $[72] !== page || $[73] !== sort || $[74] !== versions) {
    t24 = () => {
      setLoading(true);
      const controller = new AbortController();
      const params_0 = new URLSearchParams();
      if (effectiveChar) {
        params_0.set("character", effectiveChar);
      }
      if (effectiveWin) {
        params_0.set("win", effectiveWin);
      }
      if (effectiveUser) {
        params_0.set("username", effectiveUser);
      }
      if (effectiveSeed) {
        params_0.set("seed", effectiveSeed);
      }
      if (effectiveBuildId) {
        if (effectiveBuildId.includes("-") && versions.length > 0) {
          params_0.set("build_ids", expandVersionRange(effectiveBuildId, versions).join(","));
        } else {
          params_0.set("build_id", effectiveBuildId);
        }
      }
      if (effectivePlayers) {
        params_0.set("players", effectivePlayers);
      }
      if (effectiveAscension) {
        const asc = parseAscension(effectiveAscension);
        if (asc.exact !== undefined) {
          params_0.set("ascension", String(asc.exact));
        }
        if (asc.min !== undefined) {
          params_0.set("ascension_min", String(asc.min));
        }
        if (asc.max !== undefined) {
          params_0.set("ascension_max", String(asc.max));
        }
      }
      if (effectiveCard) {
        params_0.set("card", effectiveCard);
      }
      if (effectiveRelic) {
        params_0.set("relic", effectiveRelic);
      }
      if (effectiveShop) {
        params_0.set("shop", effectiveShop);
      }
      if (effectiveWinrate) {
        const wr = parseWinrate(effectiveWinrate);
        if (wr.min !== undefined) {
          params_0.set("winrate_min", String(wr.min));
        }
        if (wr.max !== undefined) {
          params_0.set("winrate_max", String(wr.max));
        }
      }
      if (effectiveGameMode === "daily_today") {
        params_0.set("game_mode", "daily");
        params_0.set("today", "true");
      } else {
        if (effectiveGameMode) {
          params_0.set("game_mode", effectiveGameMode);
        }
      }
      params_0.set("sort", sort);
      params_0.set("page", String(page));
      fetch(`${API}${config.endpoint}?${params_0}`, {
        signal: controller.signal
      }).then(_temp7).then(data_1 => {
        if (controller.signal.aborted) {
          return;
        }
        setRuns(data_1.runs || []);
        setTotal(data_1.total || 0);
        setTotalPages(data_1.total_pages || 0);
      }).catch(_temp8).finally(() => {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      });
      return () => controller.abort();
    };
    t25 = [effectiveChar, effectiveWin, effectiveUser, effectiveSeed, effectiveBuildId, effectivePlayers, effectiveGameMode, effectiveAscension, effectiveCard, effectiveRelic, effectiveShop, effectiveWinrate, versions, sort, page, config.endpoint];
    $[59] = config.endpoint;
    $[60] = effectiveAscension;
    $[61] = effectiveBuildId;
    $[62] = effectiveCard;
    $[63] = effectiveChar;
    $[64] = effectiveGameMode;
    $[65] = effectivePlayers;
    $[66] = effectiveRelic;
    $[67] = effectiveSeed;
    $[68] = effectiveShop;
    $[69] = effectiveUser;
    $[70] = effectiveWin;
    $[71] = effectiveWinrate;
    $[72] = page;
    $[73] = sort;
    $[74] = versions;
    $[75] = t24;
    $[76] = t25;
  } else {
    t24 = $[75];
    t25 = $[76];
  }
  useEffect(t24, t25);
  let t26;
  if ($[77] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = function clearAll() {
      setQuery("");
      setCharacter("");
      setWin("");
      setUser("");
      setSeed("");
      setBuildId("");
      setMode("");
      setGameMode("");
      setAscension("");
      setSort("date");
    };
    $[77] = t26;
  } else {
    t26 = $[77];
  }
  const clearAll = t26;
  const hasAnyFilter = query || character || win || user || seed || buildId || mode || gameMode || ascension || sort !== "date";
  let t27;
  if ($[78] !== config.title || $[79] !== t) {
    t27 = t(config.title);
    $[78] = config.title;
    $[79] = t;
    $[80] = t27;
  } else {
    t27 = $[80];
  }
  let t28;
  if ($[81] !== t27) {
    t28 = <h1 className="text-3xl font-bold text-[var(--accent-gold)]">{t27}</h1>;
    $[81] = t27;
    $[82] = t28;
  } else {
    t28 = $[82];
  }
  const t29 = `${bp}/leaderboards`;
  let t30;
  if ($[83] !== t) {
    t30 = t("Leaderboards");
    $[83] = t;
    $[84] = t30;
  } else {
    t30 = $[84];
  }
  let t31;
  if ($[85] !== t29 || $[86] !== t30) {
    t31 = <Link href={t29} className="text-xs text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors">{t30} →</Link>;
    $[85] = t29;
    $[86] = t30;
    $[87] = t31;
  } else {
    t31 = $[87];
  }
  let t32;
  if ($[88] !== t28 || $[89] !== t31) {
    t32 = <div className="flex items-end justify-between mb-4">{t28}{t31}</div>;
    $[88] = t28;
    $[89] = t31;
    $[90] = t32;
  } else {
    t32 = $[90];
  }
  let t33;
  if ($[91] !== config.kicker || $[92] !== t) {
    t33 = t(config.kicker);
    $[91] = config.kicker;
    $[92] = t;
    $[93] = t33;
  } else {
    t33 = $[93];
  }
  let t34;
  if ($[94] !== t33) {
    t34 = <span className="font-semibold text-[var(--text-primary)]">{t33}</span>;
    $[94] = t33;
    $[95] = t34;
  } else {
    t34 = $[95];
  }
  let t35;
  if ($[96] !== t) {
    t35 = t("Type filters into the search bar or use the dropdowns below. Combine anything:");
    $[96] = t;
    $[97] = t35;
  } else {
    t35 = $[97];
  }
  let t36;
  if ($[98] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = <code className="text-[var(--accent-gold)]">char:ironclad asc:5-10 relic:akabeko</code>;
    $[98] = t36;
  } else {
    t36 = $[98];
  }
  let t37;
  if ($[99] !== t) {
    t37 = t("finds high-ascension Ironclad runs that had Akabeko.");
    $[99] = t;
    $[100] = t37;
  } else {
    t37 = $[100];
  }
  let t38;
  if ($[101] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = <code className="text-[var(--accent-gold)]">card:bash,anger</code>;
    $[101] = t38;
  } else {
    t38 = $[101];
  }
  let t39;
  if ($[102] !== t) {
    t39 = t("requires multiple cards.");
    $[102] = t;
    $[103] = t39;
  } else {
    t39 = $[103];
  }
  let t40;
  if ($[104] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = <code className="text-[var(--accent-gold)]">asc:3-7</code>;
    $[104] = t40;
  } else {
    t40 = $[104];
  }
  let t41;
  if ($[105] !== t) {
    t41 = t("matches a range.");
    $[105] = t;
    $[106] = t41;
  } else {
    t41 = $[106];
  }
  let t42;
  if ($[107] === Symbol.for("react.memo_cache_sentinel")) {
    t42 = <code className="text-[var(--accent-gold)]">version:v0.104.0-v0.106.0</code>;
    $[107] = t42;
  } else {
    t42 = $[107];
  }
  let t43;
  if ($[108] !== t) {
    t43 = t("spans patches.");
    $[108] = t;
    $[109] = t43;
  } else {
    t43 = $[109];
  }
  let t44;
  if ($[110] !== config.outro || $[111] !== t) {
    t44 = t(config.outro);
    $[110] = config.outro;
    $[111] = t;
    $[112] = t44;
  } else {
    t44 = $[112];
  }
  let t45;
  if ($[113] !== t34 || $[114] !== t35 || $[115] !== t37 || $[116] !== t39 || $[117] !== t41 || $[118] !== t43 || $[119] !== t44) {
    t45 = <p className="text-sm text-[var(--text-secondary)] mb-4">{t34}{" "}{t35}{" "}{t36}{" "}{t37}{" "}{t38}{" "}{t39}{" "}{t40}{" "}{t41}{" "}{t42}{" "}{t43} {t44}</p>;
    $[113] = t34;
    $[114] = t35;
    $[115] = t37;
    $[116] = t39;
    $[117] = t41;
    $[118] = t43;
    $[119] = t44;
    $[120] = t45;
  } else {
    t45 = $[120];
  }
  let t46;
  if ($[121] !== config.hint || $[122] !== config.hintLink || $[123] !== t) {
    t46 = config.hint && config.hintLink && <p className="text-sm text-[var(--text-secondary)] mb-4">{t(config.hint, {
        mod: "\0"
      }).split("\0").map((part, i, parts) => <span key={i}>{part}{i < parts.length - 1 && <Link href={config.hintLink.href} className="text-[var(--accent-gold)] hover:underline">{t(config.hintLink.label)}</Link>}</span>)}</p>;
    $[121] = config.hint;
    $[122] = config.hintLink;
    $[123] = t;
    $[124] = t46;
  } else {
    t46 = $[124];
  }
  let t47;
  if ($[125] !== charName || $[126] !== config.summary) {
    t47 = config.summary && <ReplaySummary charName={charName} />;
    $[125] = charName;
    $[126] = config.summary;
    $[127] = t47;
  } else {
    t47 = $[127];
  }
  let t48;
  if ($[128] === Symbol.for("react.memo_cache_sentinel")) {
    t48 = e => setQuery(e.target.value);
    $[128] = t48;
  } else {
    t48 = $[128];
  }
  let t49;
  if ($[129] !== t) {
    t49 = t("Try: \"{example}\"", {
      example: "char:ironclad asc:10 relic:burning_blood"
    });
    $[129] = t;
    $[130] = t49;
  } else {
    t49 = $[130];
  }
  let t50;
  if ($[131] !== query || $[132] !== t49) {
    t50 = <input type="text" value={query} onChange={t48} placeholder={t49} className="w-full text-sm px-4 py-2.5 rounded-lg bg-[var(--bg-primary)] border border-[var(--border-subtle)] text-[var(--text-primary)] focus:outline-none focus:border-[var(--accent-gold)]" />;
    $[131] = query;
    $[132] = t49;
    $[133] = t50;
  } else {
    t50 = $[133];
  }
  let t51;
  if ($[134] !== t) {
    t51 = t("Expressions:");
    $[134] = t;
    $[135] = t51;
  } else {
    t51 = $[135];
  }
  let t52;
  if ($[136] === Symbol.for("react.memo_cache_sentinel")) {
    t52 = <code>user:name</code>;
    $[136] = t52;
  } else {
    t52 = $[136];
  }
  let t53;
  if ($[137] === Symbol.for("react.memo_cache_sentinel")) {
    t53 = <code>char:ironclad</code>;
    $[137] = t53;
  } else {
    t53 = $[137];
  }
  let t54;
  if ($[138] === Symbol.for("react.memo_cache_sentinel")) {
    t54 = <code>asc:10</code>;
    $[138] = t54;
  } else {
    t54 = $[138];
  }
  let t55;
  if ($[139] !== t) {
    t55 = t("or");
    $[139] = t;
    $[140] = t55;
  } else {
    t55 = $[140];
  }
  let t56;
  if ($[141] === Symbol.for("react.memo_cache_sentinel")) {
    t56 = <code>asc:3-7</code>;
    $[141] = t56;
  } else {
    t56 = $[141];
  }
  let t57;
  if ($[142] === Symbol.for("react.memo_cache_sentinel")) {
    t57 = <code>card:bash,anger</code>;
    $[142] = t57;
  } else {
    t57 = $[142];
  }
  let t58;
  if ($[143] === Symbol.for("react.memo_cache_sentinel")) {
    t58 = <code>relic:burning_blood</code>;
    $[143] = t58;
  } else {
    t58 = $[143];
  }
  let t59;
  if ($[144] !== t) {
    t59 = t("(combine for AND)");
    $[144] = t;
    $[145] = t59;
  } else {
    t59 = $[145];
  }
  let t60;
  if ($[146] === Symbol.for("react.memo_cache_sentinel")) {
    t60 = <code>shop:orange_dough</code>;
    $[146] = t60;
  } else {
    t60 = $[146];
  }
  let t61;
  if ($[147] !== t) {
    t61 = t("(bought at a shop: cards, relics, or potions)");
    $[147] = t;
    $[148] = t61;
  } else {
    t61 = $[148];
  }
  let t62;
  if ($[149] === Symbol.for("react.memo_cache_sentinel")) {
    t62 = <code>winrate:50-70</code>;
    $[149] = t62;
  } else {
    t62 = $[149];
  }
  let t63;
  if ($[150] === Symbol.for("react.memo_cache_sentinel")) {
    t63 = <code>{"winrate:>50"}</code>;
    $[150] = t63;
  } else {
    t63 = $[150];
  }
  let t64;
  if ($[151] !== t) {
    t64 = t("or");
    $[151] = t;
    $[152] = t64;
  } else {
    t64 = $[152];
  }
  let t65;
  if ($[153] === Symbol.for("react.memo_cache_sentinel")) {
    t65 = <code>winrate:100</code>;
    $[153] = t65;
  } else {
    t65 = $[153];
  }
  let t66;
  if ($[154] !== t) {
    t66 = t("(submitter win rate, min 5 runs)");
    $[154] = t;
    $[155] = t66;
  } else {
    t66 = $[155];
  }
  let t67;
  if ($[156] === Symbol.for("react.memo_cache_sentinel")) {
    t67 = <code>version:v0.106.0</code>;
    $[156] = t67;
  } else {
    t67 = $[156];
  }
  let t68;
  if ($[157] !== t) {
    t68 = t("or");
    $[157] = t;
    $[158] = t68;
  } else {
    t68 = $[158];
  }
  let t69;
  if ($[159] === Symbol.for("react.memo_cache_sentinel")) {
    t69 = <code>version:v0.104.0-v0.106.0</code>;
    $[159] = t69;
  } else {
    t69 = $[159];
  }
  let t70;
  if ($[160] === Symbol.for("react.memo_cache_sentinel")) {
    t70 = <code>seed:abc</code>;
    $[160] = t70;
  } else {
    t70 = $[160];
  }
  let t71;
  if ($[161] === Symbol.for("react.memo_cache_sentinel")) {
    t71 = <code>mode:daily</code>;
    $[161] = t71;
  } else {
    t71 = $[161];
  }
  let t72;
  if ($[162] === Symbol.for("react.memo_cache_sentinel")) {
    t72 = <code>result:win</code>;
    $[162] = t72;
  } else {
    t72 = $[162];
  }
  let t73;
  if ($[163] === Symbol.for("react.memo_cache_sentinel")) {
    t73 = <code>players:single</code>;
    $[163] = t73;
  } else {
    t73 = $[163];
  }
  let t74;
  if ($[164] !== t51 || $[165] !== t55 || $[166] !== t59 || $[167] !== t61 || $[168] !== t64 || $[169] !== t66 || $[170] !== t68) {
    t74 = <p className="mt-1.5 text-[10px] text-[var(--text-tertiary)]">{t51} {t52}, {t53}, {t54} {t55} {t56},{" "}{t57}, {t58}{" "}{t59}, {t60}{" "}{t61},{" "}{t62}, {t63}, {t64}{" "}{t65} {t66},{" "}{t67} {t68}{" "}{t69}, {t70},{" "}{t71}, {t72},{" "}{t73}</p>;
    $[164] = t51;
    $[165] = t55;
    $[166] = t59;
    $[167] = t61;
    $[168] = t64;
    $[169] = t66;
    $[170] = t68;
    $[171] = t74;
  } else {
    t74 = $[171];
  }
  let t75;
  if ($[172] !== t50 || $[173] !== t74) {
    t75 = <div className="mb-3">{t50}{t74}</div>;
    $[172] = t50;
    $[173] = t74;
    $[174] = t75;
  } else {
    t75 = $[174];
  }
  let t76;
  if ($[175] === Symbol.for("react.memo_cache_sentinel")) {
    t76 = e_0 => setCharacter(e_0.target.value);
    $[175] = t76;
  } else {
    t76 = $[175];
  }
  let t77;
  if ($[176] !== t) {
    t77 = t("All Characters");
    $[176] = t;
    $[177] = t77;
  } else {
    t77 = $[177];
  }
  let t78;
  if ($[178] !== t77) {
    t78 = <option value="">{t77}</option>;
    $[178] = t77;
    $[179] = t78;
  } else {
    t78 = $[179];
  }
  let t79;
  if ($[180] !== charName) {
    t79 = CHARACTERS.map(ch => <option key={ch} value={ch.toUpperCase()}>{stripThe(charName(ch))}</option>);
    $[180] = charName;
    $[181] = t79;
  } else {
    t79 = $[181];
  }
  let t80;
  if ($[182] !== character || $[183] !== t78 || $[184] !== t79) {
    t80 = <select value={character} onChange={t76} className="text-sm px-3 py-1.5 rounded-lg bg-[var(--bg-primary)] border border-[var(--border-subtle)] text-[var(--text-primary)] focus:outline-none focus:border-[var(--accent-gold)]">{t78}{t79}</select>;
    $[182] = character;
    $[183] = t78;
    $[184] = t79;
    $[185] = t80;
  } else {
    t80 = $[185];
  }
  let t81;
  if ($[186] === Symbol.for("react.memo_cache_sentinel")) {
    t81 = e_1 => setWin(e_1.target.value as WinFilter);
    $[186] = t81;
  } else {
    t81 = $[186];
  }
  let t82;
  if ($[187] !== t) {
    t82 = t("All Runs");
    $[187] = t;
    $[188] = t82;
  } else {
    t82 = $[188];
  }
  let t83;
  if ($[189] !== t82) {
    t83 = <option value="">{t82}</option>;
    $[189] = t82;
    $[190] = t83;
  } else {
    t83 = $[190];
  }
  let t84;
  if ($[191] !== t) {
    t84 = t("Wins");
    $[191] = t;
    $[192] = t84;
  } else {
    t84 = $[192];
  }
  let t85;
  if ($[193] !== t84) {
    t85 = <option value="true">{t84}</option>;
    $[193] = t84;
    $[194] = t85;
  } else {
    t85 = $[194];
  }
  let t86;
  if ($[195] !== t) {
    t86 = t("Losses");
    $[195] = t;
    $[196] = t86;
  } else {
    t86 = $[196];
  }
  let t87;
  if ($[197] !== t86) {
    t87 = <option value="false">{t86}</option>;
    $[197] = t86;
    $[198] = t87;
  } else {
    t87 = $[198];
  }
  let t88;
  if ($[199] !== t83 || $[200] !== t85 || $[201] !== t87 || $[202] !== win) {
    t88 = <select value={win} onChange={t81} className="text-sm px-3 py-1.5 rounded-lg bg-[var(--bg-primary)] border border-[var(--border-subtle)] text-[var(--text-primary)] focus:outline-none focus:border-[var(--accent-gold)]">{t83}{t85}{t87}</select>;
    $[199] = t83;
    $[200] = t85;
    $[201] = t87;
    $[202] = win;
    $[203] = t88;
  } else {
    t88 = $[203];
  }
  let t89;
  if ($[204] === Symbol.for("react.memo_cache_sentinel")) {
    t89 = e_2 => setMode(e_2.target.value as Mode);
    $[204] = t89;
  } else {
    t89 = $[204];
  }
  let t90;
  if ($[205] !== t) {
    t90 = t("All Modes");
    $[205] = t;
    $[206] = t90;
  } else {
    t90 = $[206];
  }
  let t91;
  if ($[207] !== t90) {
    t91 = <option value="">{t90}</option>;
    $[207] = t90;
    $[208] = t91;
  } else {
    t91 = $[208];
  }
  let t92;
  if ($[209] !== t) {
    t92 = t("Solo");
    $[209] = t;
    $[210] = t92;
  } else {
    t92 = $[210];
  }
  let t93;
  if ($[211] !== t92) {
    t93 = <option value="single">{t92}</option>;
    $[211] = t92;
    $[212] = t93;
  } else {
    t93 = $[212];
  }
  let t94;
  if ($[213] !== t) {
    t94 = t("Co-op");
    $[213] = t;
    $[214] = t94;
  } else {
    t94 = $[214];
  }
  let t95;
  if ($[215] !== t94) {
    t95 = <option value="multi">{t94}</option>;
    $[215] = t94;
    $[216] = t95;
  } else {
    t95 = $[216];
  }
  let t96;
  if ($[217] !== mode || $[218] !== t91 || $[219] !== t93 || $[220] !== t95) {
    t96 = <select value={mode} onChange={t89} className="text-sm px-3 py-1.5 rounded-lg bg-[var(--bg-primary)] border border-[var(--border-subtle)] text-[var(--text-primary)] focus:outline-none focus:border-[var(--accent-gold)]">{t91}{t93}{t95}</select>;
    $[217] = mode;
    $[218] = t91;
    $[219] = t93;
    $[220] = t95;
    $[221] = t96;
  } else {
    t96 = $[221];
  }
  let t97;
  if ($[222] === Symbol.for("react.memo_cache_sentinel")) {
    t97 = e_3 => setGameMode(e_3.target.value as GameMode);
    $[222] = t97;
  } else {
    t97 = $[222];
  }
  let t98;
  if ($[223] !== t) {
    t98 = t("Any Type");
    $[223] = t;
    $[224] = t98;
  } else {
    t98 = $[224];
  }
  let t99;
  if ($[225] !== t98) {
    t99 = <option value="">{t98}</option>;
    $[225] = t98;
    $[226] = t99;
  } else {
    t99 = $[226];
  }
  let t100;
  if ($[227] !== t) {
    t100 = t("Standard");
    $[227] = t;
    $[228] = t100;
  } else {
    t100 = $[228];
  }
  let t101;
  if ($[229] !== t100) {
    t101 = <option value="standard">{t100}</option>;
    $[229] = t100;
    $[230] = t101;
  } else {
    t101 = $[230];
  }
  let t102;
  if ($[231] !== t) {
    t102 = t("Daily");
    $[231] = t;
    $[232] = t102;
  } else {
    t102 = $[232];
  }
  let t103;
  if ($[233] !== t102) {
    t103 = <option value="daily">{t102}</option>;
    $[233] = t102;
    $[234] = t103;
  } else {
    t103 = $[234];
  }
  let t104;
  if ($[235] !== t) {
    t104 = t("Today\u2019s Daily");
    $[235] = t;
    $[236] = t104;
  } else {
    t104 = $[236];
  }
  let t105;
  if ($[237] !== t104) {
    t105 = <option value="daily_today">{t104}</option>;
    $[237] = t104;
    $[238] = t105;
  } else {
    t105 = $[238];
  }
  let t106;
  if ($[239] !== t) {
    t106 = t("Custom");
    $[239] = t;
    $[240] = t106;
  } else {
    t106 = $[240];
  }
  let t107;
  if ($[241] !== t106) {
    t107 = <option value="custom">{t106}</option>;
    $[241] = t106;
    $[242] = t107;
  } else {
    t107 = $[242];
  }
  let t108;
  if ($[243] !== gameMode || $[244] !== t101 || $[245] !== t103 || $[246] !== t105 || $[247] !== t107 || $[248] !== t99) {
    t108 = <select value={gameMode} onChange={t97} className="text-sm px-3 py-1.5 rounded-lg bg-[var(--bg-primary)] border border-[var(--border-subtle)] text-[var(--text-primary)] focus:outline-none focus:border-[var(--accent-gold)]">{t99}{t101}{t103}{t105}{t107}</select>;
    $[243] = gameMode;
    $[244] = t101;
    $[245] = t103;
    $[246] = t105;
    $[247] = t107;
    $[248] = t99;
    $[249] = t108;
  } else {
    t108 = $[249];
  }
  let t109;
  if ($[250] === Symbol.for("react.memo_cache_sentinel")) {
    t109 = e_4 => setAscension(e_4.target.value);
    $[250] = t109;
  } else {
    t109 = $[250];
  }
  let t110;
  if ($[251] !== t) {
    t110 = t("Ascension (e.g. 10, 3-7)");
    $[251] = t;
    $[252] = t110;
  } else {
    t110 = $[252];
  }
  let t111;
  if ($[253] !== ascension || $[254] !== t110) {
    t111 = <input type="text" value={ascension} onChange={t109} placeholder={t110} className="text-sm px-3 py-1.5 rounded-lg bg-[var(--bg-primary)] border border-[var(--border-subtle)] text-[var(--text-primary)] focus:outline-none focus:border-[var(--accent-gold)]" />;
    $[253] = ascension;
    $[254] = t110;
    $[255] = t111;
  } else {
    t111 = $[255];
  }
  let t112;
  if ($[256] === Symbol.for("react.memo_cache_sentinel")) {
    t112 = e_5 => setUser(e_5.target.value);
    $[256] = t112;
  } else {
    t112 = $[256];
  }
  let t113;
  if ($[257] !== t) {
    t113 = t("Username...");
    $[257] = t;
    $[258] = t113;
  } else {
    t113 = $[258];
  }
  let t114;
  if ($[259] !== t113 || $[260] !== user) {
    t114 = <input type="text" value={user} onChange={t112} placeholder={t113} className="text-sm px-3 py-1.5 rounded-lg bg-[var(--bg-primary)] border border-[var(--border-subtle)] text-[var(--text-primary)] focus:outline-none focus:border-[var(--accent-gold)]" />;
    $[259] = t113;
    $[260] = user;
    $[261] = t114;
  } else {
    t114 = $[261];
  }
  let t115;
  if ($[262] === Symbol.for("react.memo_cache_sentinel")) {
    t115 = e_6 => setSeed(e_6.target.value);
    $[262] = t115;
  } else {
    t115 = $[262];
  }
  let t116;
  if ($[263] !== t) {
    t116 = t("Seed...");
    $[263] = t;
    $[264] = t116;
  } else {
    t116 = $[264];
  }
  let t117;
  if ($[265] !== seed || $[266] !== t116) {
    t117 = <input type="text" value={seed} onChange={t115} placeholder={t116} className="text-sm px-3 py-1.5 rounded-lg bg-[var(--bg-primary)] border border-[var(--border-subtle)] text-[var(--text-primary)] focus:outline-none focus:border-[var(--accent-gold)]" />;
    $[265] = seed;
    $[266] = t116;
    $[267] = t117;
  } else {
    t117 = $[267];
  }
  let t118;
  if ($[268] !== buildId || $[269] !== t || $[270] !== versions) {
    t118 = versions.length > 0 && <select value={buildId} onChange={e_7 => setBuildId(e_7.target.value)} className="text-sm px-3 py-1.5 rounded-lg bg-[var(--bg-primary)] border border-[var(--border-subtle)] text-[var(--text-primary)] focus:outline-none focus:border-[var(--accent-gold)]"><option value="">{t("All Versions")}</option>{versions.map(_temp9)}</select>;
    $[268] = buildId;
    $[269] = t;
    $[270] = versions;
    $[271] = t118;
  } else {
    t118 = $[271];
  }
  let t119;
  if ($[272] === Symbol.for("react.memo_cache_sentinel")) {
    t119 = e_8 => setSort(e_8.target.value as Sort);
    $[272] = t119;
  } else {
    t119 = $[272];
  }
  let t120;
  if ($[273] !== t) {
    t120 = t("Newest");
    $[273] = t;
    $[274] = t120;
  } else {
    t120 = $[274];
  }
  let t121;
  if ($[275] !== t120) {
    t121 = <option value="date">{t120}</option>;
    $[275] = t120;
    $[276] = t121;
  } else {
    t121 = $[276];
  }
  let t122;
  if ($[277] !== t) {
    t122 = t("Fastest");
    $[277] = t;
    $[278] = t122;
  } else {
    t122 = $[278];
  }
  let t123;
  if ($[279] !== t122) {
    t123 = <option value="time_asc">{t122}</option>;
    $[279] = t122;
    $[280] = t123;
  } else {
    t123 = $[280];
  }
  let t124;
  if ($[281] !== t) {
    t124 = t("Slowest");
    $[281] = t;
    $[282] = t124;
  } else {
    t124 = $[282];
  }
  let t125;
  if ($[283] !== t124) {
    t125 = <option value="time_desc">{t124}</option>;
    $[283] = t124;
    $[284] = t125;
  } else {
    t125 = $[284];
  }
  let t126;
  if ($[285] !== t) {
    t126 = t("Highest Asc");
    $[285] = t;
    $[286] = t126;
  } else {
    t126 = $[286];
  }
  let t127;
  if ($[287] !== t126) {
    t127 = <option value="ascension_desc">{t126}</option>;
    $[287] = t126;
    $[288] = t127;
  } else {
    t127 = $[288];
  }
  let t128;
  if ($[289] !== sort || $[290] !== t121 || $[291] !== t123 || $[292] !== t125 || $[293] !== t127) {
    t128 = <select value={sort} onChange={t119} className="text-sm px-3 py-1.5 rounded-lg bg-[var(--bg-primary)] border border-[var(--border-subtle)] text-[var(--text-primary)] focus:outline-none focus:border-[var(--accent-gold)]">{t121}{t123}{t125}{t127}</select>;
    $[289] = sort;
    $[290] = t121;
    $[291] = t123;
    $[292] = t125;
    $[293] = t127;
    $[294] = t128;
  } else {
    t128 = $[294];
  }
  let t129;
  if ($[295] !== hasAnyFilter || $[296] !== t) {
    t129 = hasAnyFilter && <button onClick={clearAll} className="text-xs px-3 py-1.5 rounded-lg border border-[var(--border-subtle)] text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:border-[var(--border-accent)] transition-colors">{t("Clear")}</button>;
    $[295] = hasAnyFilter;
    $[296] = t;
    $[297] = t129;
  } else {
    t129 = $[297];
  }
  let t130;
  if ($[298] !== t108 || $[299] !== t111 || $[300] !== t114 || $[301] !== t117 || $[302] !== t118 || $[303] !== t128 || $[304] !== t129 || $[305] !== t80 || $[306] !== t88 || $[307] !== t96) {
    t130 = <div className="grid grid-cols-2 gap-2 mb-4 sm:flex sm:flex-wrap">{t80}{t88}{t96}{t108}{t111}{t114}{t117}{t118}{t128}{t129}</div>;
    $[298] = t108;
    $[299] = t111;
    $[300] = t114;
    $[301] = t117;
    $[302] = t118;
    $[303] = t128;
    $[304] = t129;
    $[305] = t80;
    $[306] = t88;
    $[307] = t96;
    $[308] = t130;
  } else {
    t130 = $[308];
  }
  let t131;
  if ($[309] !== total) {
    t131 = total.toLocaleString();
    $[309] = total;
    $[310] = t131;
  } else {
    t131 = $[310];
  }
  let t132;
  if ($[311] !== config.totalLabel || $[312] !== t) {
    t132 = t(config.totalLabel);
    $[311] = config.totalLabel;
    $[312] = t;
    $[313] = t132;
  } else {
    t132 = $[313];
  }
  let t133;
  if ($[314] !== t131 || $[315] !== t132) {
    t133 = <p className="text-xs text-[var(--text-muted)] mb-3">{t131} {t132}</p>;
    $[314] = t131;
    $[315] = t132;
    $[316] = t133;
  } else {
    t133 = $[316];
  }
  let t134;
  if ($[317] !== bp || $[318] !== charName || $[319] !== config || $[320] !== loading || $[321] !== page || $[322] !== runs || $[323] !== t || $[324] !== totalPages) {
    t134 = loading ? <div className="space-y-2">{[...Array(8)].map(_temp10)}</div> : runs.length === 0 ? <p className="text-center py-8 text-[var(--text-muted)]">{t(config.emptyLabel)}</p> : <><div className="space-y-2">{runs.map(r_2 => <Link prefetch={false} key={r_2.run_hash} href={browseRowHref(config, bp, r_2.run_hash)} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-3 bg-[var(--bg-card)] rounded-lg border border-[var(--border-subtle)] px-3 sm:px-4 py-3 hover:bg-[var(--bg-card-hover)] transition-colors"><div className="flex items-center gap-2 sm:gap-3 min-w-0"><span className={`text-sm font-medium shrink-0 ${r_2.win ? "text-[var(--color-silent)]" : "text-[var(--color-ironclad)]"}`}>{r_2.win ? "W" : r_2.was_abandoned ? "A" : "L"}</span><span className="text-sm text-[var(--text-primary)] truncate">{stripThe(charName(r_2.character))}</span><span className="text-xs text-[var(--text-muted)] shrink-0">A{r_2.ascension}</span>{r_2.username && <span className="text-xs text-[var(--accent-gold)] truncate">{r_2.username}</span>}</div><div className="flex items-center gap-3 sm:gap-4 text-xs text-[var(--text-muted)] shrink-0"><span className="hidden sm:inline">{t("{n} cards", {
                n: r_2.deck_size
              })}</span><span className="hidden sm:inline">{t("{n} relics", {
                n: r_2.relic_count
              })}</span><span>{r_2.floors_reached}f</span><span>{formatTimeShort(r_2.run_time)}</span>{config.watch && <span className="rounded-md border border-[var(--accent-gold)] px-2 py-0.5 text-[11px] font-semibold text-[var(--accent-gold)]">▶ {t("Watch")}</span>}</div></Link>)}</div>{totalPages > 1 && <div className="flex items-center justify-center gap-2 mt-4"><button onClick={() => setPage(_temp11)} disabled={page <= 1} className="text-xs px-3 py-1.5 rounded-lg border border-[var(--border-subtle)] text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:border-[var(--border-accent)] transition-colors disabled:opacity-30 disabled:cursor-not-allowed">← {t("Prev")}</button><span className="text-xs text-[var(--text-muted)]">{t("Page")} {page} {t("of")} {totalPages}</span><button onClick={() => setPage(p_0 => Math.min(totalPages, p_0 + 1))} disabled={page >= totalPages} className="text-xs px-3 py-1.5 rounded-lg border border-[var(--border-subtle)] text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:border-[var(--border-accent)] transition-colors disabled:opacity-30 disabled:cursor-not-allowed">{t("Next")} →</button></div>}</>;
    $[317] = bp;
    $[318] = charName;
    $[319] = config;
    $[320] = loading;
    $[321] = page;
    $[322] = runs;
    $[323] = t;
    $[324] = totalPages;
    $[325] = t134;
  } else {
    t134 = $[325];
  }
  let t135;
  if ($[326] !== t130 || $[327] !== t133 || $[328] !== t134 || $[329] !== t32 || $[330] !== t45 || $[331] !== t46 || $[332] !== t47 || $[333] !== t75) {
    t135 = <div className="mx-auto max-w-[1400px] px-3 sm:px-5 py-6">{t32}{t45}{t46}{t47}{t75}{t130}{t133}{t134}</div>;
    $[326] = t130;
    $[327] = t133;
    $[328] = t134;
    $[329] = t32;
    $[330] = t45;
    $[331] = t46;
    $[332] = t47;
    $[333] = t75;
    $[334] = t135;
  } else {
    t135 = $[334];
  }
  return t135;
}

// useSearchParams needs a Suspense boundary above it now that the root
// layout no longer provides one (the app-wide boundary made every dynamic
// page's body invisible to non-JS crawlers). The boundary lives here so
// every page that renders this client, English and localized, gets it.
function _temp11(p) {
  return Math.max(1, p - 1);
}
function _temp10(_, i_0) {
  return <div key={i_0} className="h-14 bg-[var(--bg-card)] rounded-lg animate-pulse" />;
}
function _temp9(v_4) {
  return <option key={v_4} value={v_4}>{v_4}</option>;
}
function _temp8() {}
function _temp7(r_1) {
  return r_1.ok ? r_1.json() : {
    runs: [],
    total: 0,
    total_pages: 0
  };
}
function _temp6() {}
function _temp5(r_0) {
  return r_0.ok ? r_0.json() : [];
}
function _temp4() {}
function _temp3(a, b) {
  return b.localeCompare(a, undefined, {
    numeric: true
  });
}
function _temp2(v_3) {
  return !v_3.toLowerCase().includes("nonreleased");
}
function _temp(r) {
  return r.ok ? r.json() : {
    versions: []
  };
}
export default function BrowseRunsClient(t0) {
  const $ = _c(10);
  const {
    config: t1
  } = t0;
  const config = t1 === undefined ? RUNS_BROWSE : t1;
  const t = useT();
  useGameLocale();
  let t2;
  if ($[0] !== config.title || $[1] !== t) {
    t2 = t(config.title);
    $[0] = config.title;
    $[1] = t;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t2) {
    t3 = <div className="mx-auto max-w-[1400px] px-3 sm:px-5 py-6"><h1 className="text-3xl font-bold text-[var(--accent-gold)]">{t2}</h1></div>;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== config) {
    t4 = <BrowseRunsClientInner config={config} />;
    $[5] = config;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t3 || $[8] !== t4) {
    t5 = <Suspense fallback={t3}>{t4}</Suspense>;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
}